import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from 'components/ui/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import ContactsTable from './components/ContactsTable';
import ContactsFormModal from './components/ContactsFormModal';
import ConvertDealModal from './components/ConvertDealModal';
import { contactsApi } from 'api/contacts';
import { pipelineApi } from 'api/deals';
import { getCurrentUser } from 'api/client';
import { useToast } from 'components/ui/Toast';

export default function Contacts() {
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === 'Owner' || currentUser?.role === 'Manager';
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [stageConfig, setStageConfig] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadContactsData = async () => {
    const [statsResponse, contactsResponse, configResponse] = await Promise.all([
      contactsApi.getStats(),
      contactsApi.getAll(),
      pipelineApi.getConfig().catch(() => null),
    ]);

    return {
      statsResponse,
      contactsResponse: contactsResponse || [],
      configResponse,
    };
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await loadContactsData();

        if (!isMounted) {
          return;
        }

        setStats(data.statsResponse);
        setContacts(data.contactsResponse);
        setStageConfig(data.configResponse);
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || 'Unable to load contacts.');
          setStats(null);
          setContacts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const buildContactPayload = (contact) => ({
    fullName: contact?.name?.trim(),
    email: contact?.email?.trim() || undefined,
    phone: contact?.phone?.trim() || undefined,
    source: contact?.source || 'Website',
    whatsappConsent: contact?.whatsappConsent || 'unknown',
    tags: String(contact?.tags || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
  });

  const handleContactSubmit = async (formData) => {
    const payload = buildContactPayload(formData);
    setFormError("");
    try {
      if (selectedContact?.id) {
        await contactsApi.update(selectedContact.id, payload);
        toast.success('Contact updated.');
      } else {
        await contactsApi.create(payload);
        toast.success('Contact created.');
      }
    } catch (error) {
      setFormError(error?.message || "Could not save the contact. Please try again.");
      return;
    }

    const data = await loadContactsData();
    setStats(data.statsResponse);
    setContacts(data.contactsResponse);
    setIsFormOpen(false);
    setSelectedContact(null);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactsApi.remove(contactId);
      toast.success('Contact deleted.');
      const data = await loadContactsData();
      setStats(data.statsResponse);
      setContacts(data.contactsResponse);
    } catch (error) {
      toast.error(error?.message || 'Could not delete the contact.');
    }
  };

  const handleConvertSubmit = async (formData) => {
    setFormError("");
    try {
      await contactsApi.convert(selectedContact?.id, formData);
      toast.success('Contact converted to a deal.');
    } catch (error) {
      setFormError(error?.message || "Could not convert the contact. Please try again.");
      return;
    }

    const data = await loadContactsData();
    setStats(data.statsResponse);
    setContacts(data.contactsResponse);
    setIsConvertOpen(false);
    setSelectedContact(null);
  };

  const statsCards = useMemo(() => {
    if (!stats) {
      return undefined;
    }

    return [
      { label: 'Total Contacts', value: String(stats.totalContacts), change: null, icon: 'Users', color: '#3B82F6', bg: '#DBEAFE' },
      { label: 'Converted to Deal', value: String(stats.converted), change: null, icon: 'TrendingUp', color: '#15803D', bg: '#DCFCE7' },
      { label: 'WhatsApp Consent', value: String(stats.consented), change: null, icon: 'MessageCircle', color: '#7C3AED', bg: '#EDE9FE' },
    ];
  }, [stats]);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-heading font-bold text-2xl md:text-3xl"
              style={{ color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}
            >
              Contacts
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              People in your CRM — add, edit, and convert them into deals
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={14}
            onClick={() => {
              setSelectedContact(null);
              setIsFormOpen(true);
            }}
          >
            Add Contact
          </Button>
        </div>

        {formError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{formError}</p>
          </div>
        )}

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
              onClick={async () => {
                setIsLoading(true);
                setLoadError('');
                try {
                  const data = await loadContactsData();
                  setStats(data.statsResponse);
                  setContacts(data.contactsResponse);
                  setStageConfig(data.configResponse);
                } catch (error) {
                  setLoadError(error?.message || 'Unable to load contacts.');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {statsCards && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {statsCards.map((card) => (
              <div key={card.label} className="rounded-xl p-4 flex items-center gap-4" style={{ border: '1px solid var(--color-border)', background: 'var(--color-card)' }}>
                <div className="flex items-center justify-center rounded-xl" style={{ width: 44, height: 44, background: card.bg }}>
                  <Icon name={card.icon} size={20} color={card.color} />
                </div>
                <div className="flex-1">
                  <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>{card.label}</p>
                  <p className="font-heading font-bold text-xl" style={{ color: 'var(--color-foreground)' }}>{card.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="relative mb-4">
          <Icon
            name="Search"
            size={16}
            color="var(--color-text-secondary)"
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search contacts by name, email, phone, source, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border font-caption text-sm transition-colors"
            style={{
              borderColor: 'var(--color-border)',
              background: 'var(--color-card)',
              color: 'var(--color-foreground)',
              outline: 'none',
            }}
            onFocus={(e) => (e.target.style.borderColor = 'var(--color-primary)')}
            onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
          />
          {searchQuery && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2"
              onClick={() => setSearchQuery('')}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <Icon name="X" size={14} color="currentColor" />
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading contacts...</p>
          </div>
        ) : (
        <ContactsTable
          searchQuery={searchQuery}
          initialContacts={contacts}
          canDelete={isManager}
          onEditContact={(contact) => {
            setSelectedContact(contact);
            setFormError("");
            setIsFormOpen(true);
          }}
          onConvertContact={(contact) => {
            setSelectedContact(contact);
            setFormError("");
            setIsConvertOpen(true);
          }}
          onDeleteContact={handleDeleteContact}
          onAddNew={() => {
            setSelectedContact(null);
            setIsFormOpen(true);
          }}
        />
        )}

        <ContactsFormModal
          isOpen={isFormOpen}
          contact={selectedContact}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedContact(null);
            setFormError("");
          }}
          onSubmit={handleContactSubmit}
        />

        <ConvertDealModal
          isOpen={isConvertOpen}
          contact={selectedContact}
          stages={stageConfig?.stages}
          onClose={() => {
            setIsConvertOpen(false);
            setSelectedContact(null);
            setFormError("");
          }}
          onSubmit={handleConvertSubmit}
        />

        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>SSL Secured</span>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}